import {
    ReadonlyMat3,
    ReadonlyVec2,
    ReadonlyVec3,
    mat3,
    vec2,
    vec3,
} from 'gl-matrix';
import { DomainValidationError } from '../errors/DomainError';
import { Modality, isImageModality } from '../dicom/modality';
import { SliceGeometry, SliceGeometryInventory } from './sliceGeometry';
import { WindowLevel } from './windowLevel';

/**
 * One slice of an {@link ImageSeriesManifest}: the geometry the connector
 * database knows about a slice *before* its pixels have been retrieved.
 *
 * Positions are mm in the DICOM patient coordinate system.
 */
export interface ImageSeriesManifestSlice {
    /** DICOM SOP Instance UID (0008,0018). Identity only — never logged. */
    readonly sopInstanceUid: string;
    /** DICOM Instance Number (0020,0013). */
    readonly instanceNumber: number;
    /** DICOM Slice Location (0020,1041), mm. Reported, not used for geometry. */
    readonly sliceLocationMm: number;
    /** Image Position Patient (0020,0032), mm, centre of the first voxel. */
    readonly imagePositionPatientMm: ReadonlyVec3;
}

/** Constructor input for {@link ImageSeriesManifest}. */
export interface ImageSeriesManifestInit {
    readonly studyInstanceUid: string;
    readonly seriesInstanceUid: string;
    readonly frameOfReferenceUid: string;
    readonly modality: Modality;
    /** DICOM Rows (0028,0010) — the volume's Y size in voxels. */
    readonly rows: number;
    /** DICOM Columns (0028,0011) — the volume's X size in voxels. */
    readonly columns: number;
    /**
     * DICOM Pixel Spacing (0028,0030), mm, in tag order
     * `[row spacing, column spacing]` — the same order and the same
     * uniform-spacing restriction the DICOM parser applies, so a
     * manifest-derived grid cannot diverge from a parse-derived one.
     */
    readonly pixelSpacingMm: ReadonlyVec2;
    /** Image Orientation Patient (0020,0037) as direction cosines. */
    readonly imageOrientationPatient: ReadonlyMat3;
    /** DICOM Slice Thickness (0018,0050), mm. */
    readonly sliceThicknessMm: number;
    /**
     * Acquisition window/level from the manifest, when the series declares
     * one. Seeds the progressive image's frozen display state; absent means
     * the series carried no Window Center / Window Width.
     */
    readonly acquisitionWindowLevel?: WindowLevel;
    /** DICOM Rescale Slope (0028,1053), when declared. */
    readonly rescaleSlope?: number;
    /** DICOM Rescale Intercept (0028,1052), when declared. */
    readonly rescaleIntercept?: number;
    /**
     * How many slices the source declares the series should contain, when it
     * declares a count. May exceed `slices.length` while a C-STORE push is
     * still arriving — see {@link ImageSeriesManifest.isInventoryComplete}.
     */
    readonly expectedSliceCount?: number;

    /**
     * Whether the source is still storing instances into this series. Absent
     * is read as "not receiving": a source that does not report it cannot be
     * mid-push in a way this client can observe.
     */
    readonly isReceiving?: boolean;
    /** Every slice the source currently knows about. At least one. */
    readonly slices: readonly ImageSeriesManifestSlice[];
}

/** In-plane spacing must be square to the same tolerance the parser applies. */
const PIXEL_SPACING_UNIFORMITY_TOLERANCE_MM = 1e-2;

/** Direction cosines must be unit-length and orthogonal to this tolerance. */
const ORIENTATION_TOLERANCE = 1e-3;

function requireNonEmpty(value: string, field: string): string {
    if (value.trim() === '') {
        throw new DomainValidationError(
            `Image series manifest is missing ${field}`
        );
    }
    return value;
}

function requirePositiveInteger(value: number, field: string): number {
    if (!Number.isInteger(value) || value <= 0) {
        throw new DomainValidationError(
            `Image series manifest ${field} must be a positive integer, got ${value}`
        );
    }
    return value;
}

function requireFiniteVec3(value: ReadonlyVec3, field: string): vec3 {
    if (
        value.length !== 3 ||
        !Number.isFinite(value[0]) ||
        !Number.isFinite(value[1]) ||
        !Number.isFinite(value[2])
    ) {
        throw new DomainValidationError(
            `Image series manifest ${field} is not a finite 3D position`
        );
    }
    return vec3.fromValues(value[0], value[1], value[2]);
}

/**
 * Geometry of one image series as the connector database knows it, *before*
 * any pixel data has been retrieved: enough to build the volume's {@link Grid},
 * size its buffers and enumerate what is still missing.
 *
 * Pure domain: typed fields, `vec` positions, numeric spacing in mm. It holds
 * no DTO shapes and no backslash-joined DICOM multi-value strings — parsing
 * those is the adapter's job at the infrastructure boundary
 * (`~/backend/connector-next-api/imageManifest`).
 *
 * Every invariant is enforced in the constructor: a manifest that exists is a
 * manifest a volume can be built from.
 */
export class ImageSeriesManifest {
    public readonly studyInstanceUid: string;
    public readonly seriesInstanceUid: string;
    public readonly frameOfReferenceUid: string;
    public readonly modality: Modality;
    public readonly rows: number;
    public readonly columns: number;
    public readonly pixelSpacingMm: ReadonlyVec2;
    public readonly imageOrientationPatient: ReadonlyMat3;
    public readonly sliceThicknessMm: number;
    public readonly acquisitionWindowLevel: WindowLevel | undefined;
    public readonly rescaleSlope: number | undefined;
    public readonly rescaleIntercept: number | undefined;
    public readonly expectedSliceCount: number | undefined;

    /** Whether the source is still storing instances into this series. */
    public readonly isReceiving: boolean;

    /**
     * Every slice of the series, ordered ascending by the projection of its
     * Image Position Patient onto the stack normal, with the SOP Instance UID
     * as a total-order tie-break. The ordering is deterministic and
     * independent of the order the source supplied.
     */
    public readonly slices: readonly ImageSeriesManifestSlice[];

    public constructor(init: ImageSeriesManifestInit) {
        this.studyInstanceUid = requireNonEmpty(
            init.studyInstanceUid,
            'a Study Instance UID'
        );
        this.seriesInstanceUid = requireNonEmpty(
            init.seriesInstanceUid,
            'a Series Instance UID'
        );
        this.frameOfReferenceUid = requireNonEmpty(
            init.frameOfReferenceUid,
            'a Frame of Reference UID'
        );

        if (!isImageModality(init.modality)) {
            throw new DomainValidationError(
                `Image series manifest modality ${init.modality} cannot form a 3D image`
            );
        }
        this.modality = init.modality;

        this.rows = requirePositiveInteger(init.rows, 'Rows');
        this.columns = requirePositiveInteger(init.columns, 'Columns');

        if (
            init.pixelSpacingMm.length !== 2 ||
            !Number.isFinite(init.pixelSpacingMm[0]) ||
            !Number.isFinite(init.pixelSpacingMm[1]) ||
            init.pixelSpacingMm[0] <= 0 ||
            init.pixelSpacingMm[1] <= 0
        ) {
            throw new DomainValidationError(
                'Image series manifest Pixel Spacing must be two positive values in mm'
            );
        }
        if (
            Math.abs(init.pixelSpacingMm[0] - init.pixelSpacingMm[1]) >=
            PIXEL_SPACING_UNIFORMITY_TOLERANCE_MM
        ) {
            // Mirrors the DICOM parser: only square in-plane spacing is
            // supported, and the two paths must accept exactly the same series.
            throw new DomainValidationError(
                `Non-uniform pixel spacing: ${init.pixelSpacingMm[0].toFixed(3)}, ` +
                    `${init.pixelSpacingMm[1].toFixed(3)}`
            );
        }
        this.pixelSpacingMm = vec2.fromValues(
            init.pixelSpacingMm[0],
            init.pixelSpacingMm[1]
        );

        this.imageOrientationPatient = ImageSeriesManifest.validateOrientation(
            init.imageOrientationPatient
        );

        if (
            !Number.isFinite(init.sliceThicknessMm) ||
            init.sliceThicknessMm <= 0
        ) {
            throw new DomainValidationError(
                `Image series manifest Slice Thickness must be positive, got ${init.sliceThicknessMm}`
            );
        }
        this.sliceThicknessMm = init.sliceThicknessMm;

        this.acquisitionWindowLevel = init.acquisitionWindowLevel;
        this.rescaleSlope = init.rescaleSlope;
        this.rescaleIntercept = init.rescaleIntercept;

        if (init.expectedSliceCount !== undefined) {
            requirePositiveInteger(
                init.expectedSliceCount,
                'expected slice count'
            );
        }
        this.expectedSliceCount = init.expectedSliceCount;
        this.isReceiving = init.isReceiving ?? false;

        this.slices = ImageSeriesManifest.validateSlices(
            init.slices,
            this.imageOrientationPatient
        );
    }

    private static validateOrientation(orientation: ReadonlyMat3): mat3 {
        if (orientation.length !== 9) {
            throw new DomainValidationError(
                'Image series manifest Image Orientation Patient must be a 3x3 direction-cosine matrix'
            );
        }
        for (const component of orientation) {
            if (!Number.isFinite(component)) {
                throw new DomainValidationError(
                    'Image series manifest Image Orientation Patient contains a non-finite value'
                );
            }
        }
        const rowCosines = vec3.fromValues(
            orientation[0],
            orientation[1],
            orientation[2]
        );
        const colCosines = vec3.fromValues(
            orientation[3],
            orientation[4],
            orientation[5]
        );
        if (
            Math.abs(vec3.length(rowCosines) - 1) > ORIENTATION_TOLERANCE ||
            Math.abs(vec3.length(colCosines) - 1) > ORIENTATION_TOLERANCE
        ) {
            throw new DomainValidationError(
                'Image series manifest Image Orientation Patient direction cosines are not unit vectors'
            );
        }
        if (
            Math.abs(vec3.dot(rowCosines, colCosines)) > ORIENTATION_TOLERANCE
        ) {
            throw new DomainValidationError(
                'Image series manifest Image Orientation Patient direction cosines are not orthogonal'
            );
        }
        return mat3.clone(orientation);
    }

    private static validateSlices(
        slices: readonly ImageSeriesManifestSlice[],
        orientation: ReadonlyMat3
    ): readonly ImageSeriesManifestSlice[] {
        if (slices.length === 0) {
            throw new DomainValidationError(
                'Image series manifest contains no slices'
            );
        }

        const zDirection = vec3.fromValues(
            orientation[6],
            orientation[7],
            orientation[8]
        );

        const seen = new Set<string>();
        const validated = slices.map((slice, index) => {
            const sopInstanceUid = slice.sopInstanceUid?.trim() ?? '';
            if (sopInstanceUid === '') {
                // Index, never the UID: manifest errors reach logs and toasts.
                throw new DomainValidationError(
                    `Image series manifest slice at index ${index} has no SOP Instance UID`
                );
            }
            if (seen.has(sopInstanceUid)) {
                throw new DomainValidationError(
                    `Image series manifest slice at index ${index} repeats a SOP Instance UID`
                );
            }
            seen.add(sopInstanceUid);

            if (!Number.isFinite(slice.instanceNumber)) {
                throw new DomainValidationError(
                    `Image series manifest slice at index ${index} has no Instance Number`
                );
            }
            if (!Number.isFinite(slice.sliceLocationMm)) {
                throw new DomainValidationError(
                    `Image series manifest slice at index ${index} has no Slice Location`
                );
            }

            return {
                sopInstanceUid,
                instanceNumber: slice.instanceNumber,
                sliceLocationMm: slice.sliceLocationMm,
                imagePositionPatientMm: requireFiniteVec3(
                    slice.imagePositionPatientMm,
                    `slice at index ${index} Image Position Patient`
                ),
            };
        });

        // Deterministic total order: z-projection first, UID as tie-break so
        // two equal projections can never swap between runs.
        validated.sort((a, b) => {
            const za = vec3.dot(zDirection, a.imagePositionPatientMm);
            const zb = vec3.dot(zDirection, b.imagePositionPatientMm);
            if (za !== zb) {
                return za - zb;
            }
            return a.sopInstanceUid < b.sopInstanceUid ? -1 : 1;
        });

        return Object.freeze(validated);
    }

    /** Number of slices actually listed in this manifest. */
    public get sliceCount(): number {
        return this.slices.length;
    }

    /**
     * Whether the listed slices are the whole series, so a volume may be built
     * from them.
     *
     * `false` means the source is still receiving it — a C-STORE push in
     * flight. A volume must NOT be built from such a manifest: the grid would
     * be sized to a partial inventory and `findBaseSpacing` could infer a
     * spacing that later slices contradict. Re-fetch the manifest instead.
     *
     * Two independent facts have to agree, because they cover different
     * sources. A retrieval from a remote node knows its total up front, and
     * `expectedSliceCount` compares it against what has landed. A push
     * announces no total at all — every instance stored so far looks like a
     * complete series — so nothing can be derived from counts, and the source
     * reports `isReceiving` for as long as the association storing them is
     * open. An inventory is settled only when neither says otherwise.
     */
    public get isInventoryComplete(): boolean {
        if (this.isReceiving) {
            return false;
        }
        return (
            this.expectedSliceCount === undefined ||
            this.expectedSliceCount === this.slices.length
        );
    }

    /**
     * The FULL slice inventory in the structural shape
     * {@link validateSlicePositions} consumes — the same input a parsed
     * `Series` provides, so both go through one validation and one grid
     * builder. Never call this with a filtered subset: the caller cannot,
     * because the manifest holds no arrival state.
     */
    public toSliceGeometryInventory(): SliceGeometryInventory {
        const geometries: SliceGeometry[] = this.slices.map((slice) => ({
            sopInstanceUid: slice.sopInstanceUid,
            imagePosition: slice.imagePositionPatientMm,
            imageOrientation: this.imageOrientationPatient,
            sliceThickness: this.sliceThicknessMm,
        }));
        return {
            is3DImage: isImageModality(this.modality),
            dicomImageSlices: geometries,
        };
    }
}
